import { useEffect, useState } from "react";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Megaphone, Plus, Trash2, ImagePlus, Star, Search } from "lucide-react";

export const imgSrc = (url) => (url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);
const EMPTY = { title: "", text: "", price: "", active: true };

const PromoForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [file, setFile] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="tv-promo-form">
      <Input placeholder="Título (ej. Recargas Kölbi)" value={f.title} onChange={set("title")} maxLength={80} data-testid="tv-promo-title" />
      <Input placeholder="Texto corto (opcional)" value={f.text} onChange={set("text")} maxLength={200} data-testid="tv-promo-text" />
      <Input placeholder="Precio o detalle (ej. ₡1.000 / 2x1)" value={f.price} onChange={set("price")} maxLength={40} data-testid="tv-promo-price" />
      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
        <ImagePlus className="w-4 h-4" /> {file ? file.name : "Foto (opcional)"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="tv-promo-image" />
      </label>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancelar</Button>
        <Button size="sm" disabled={!f.title.trim()} onClick={() => onSave(f, file)} data-testid="tv-promo-save">Guardar</Button>
      </div>
    </div>
  );
};

const PromosTab = ({ promos, reload }) => {
  const [editing, setEditing] = useState(null);
  const save = async (f, file) => {
    try {
      const body = { title: f.title, text: f.text || "", price: f.price || "", active: f.active };
      const r = editing === "new" ? await axiosInstance.post("/lottery/tv-promos", body) : await axiosInstance.put(`/lottery/tv-promos/${editing}`, body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await axiosInstance.post(`/lottery/tv-promos/${r.data.id}/image`, fd);
      }
      toast.success("Promoción guardada");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };
  const toggle = async (p) => { await axiosInstance.put(`/lottery/tv-promos/${p.id}`, { title: p.title, text: p.text, price: p.price, active: !p.active }); reload(); };
  const remove = async (p) => { if (window.confirm(`¿Eliminar "${p.title}"?`)) { await axiosInstance.delete(`/lottery/tv-promos/${p.id}`); reload(); } };
  return (
    <div className="space-y-3">
      {editing ? (
        <PromoForm initial={editing === "new" ? EMPTY : promos.find((p) => p.id === editing)} onSave={save} onCancel={() => setEditing(null)} />
      ) : (
        <Button size="sm" className="gap-1" onClick={() => setEditing("new")} data-testid="tv-promo-add"><Plus className="w-4 h-4" /> Nueva promoción</Button>
      )}
      {promos.length === 0 && !editing && <p className="text-sm text-slate-500">Aún no hay promociones.</p>}
      {promos.map((p) => (
        <div key={p.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2" data-testid={`tv-promo-${p.id}`}>
          <div className="w-14 h-14 rounded bg-slate-100 overflow-hidden shrink-0">{p.image_url && <img src={imgSrc(p.image_url)} alt="" className="w-full h-full object-cover" />}</div>
          <button className="flex-1 min-w-0 text-left" onClick={() => setEditing(p.id)}>
            <p className="font-semibold text-sm truncate">{p.title}</p>
            <p className="text-xs text-slate-500 truncate">{[p.price, p.text].filter(Boolean).join(" · ")}</p>
          </button>
          <Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`tv-promo-active-${p.id}`} />
          <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(p)} data-testid={`tv-promo-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
    </div>
  );
};

const ProductsTab = ({ featured, reload }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => axiosInstance.get(`/lottery/tv-products?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)), 300);
    return () => clearTimeout(t);
  }, [q]);
  const ids = new Set(featured.map((p) => p.id));
  const toggle = async (p) => {
    await axiosInstance.put(`/lottery/tv-products/${p.id}`, { featured: !ids.has(p.id) });
    reload();
  };
  const Row = ({ p }) => (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-2">
      <div className="w-10 h-10 rounded bg-slate-100 overflow-hidden shrink-0">{p.image_url && <img src={imgSrc(p.image_url)} alt="" className="w-full h-full object-cover" />}</div>
      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{p.name}</p><p className="text-xs text-slate-500">₡{Number(p.price || 0).toLocaleString()}</p></div>
      <Button size="icon" variant="ghost" onClick={() => toggle(p)} data-testid={`tv-product-star-${p.id}`}>
        <Star className={`w-5 h-5 ${ids.has(p.id) ? "fill-amber-400 text-amber-500" : "text-slate-400"}`} />
      </Button>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" /><Input className="pl-9" placeholder="Buscar producto por nombre o código…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tv-product-search" /></div>
      {results.map((p) => <Row key={`r-${p.id}`} p={p} />)}
      <p className="text-xs font-semibold uppercase text-slate-500 pt-2">Destacados en TV ({featured.length})</p>
      {featured.length === 0 && <p className="text-sm text-slate-500">Busca un producto y toca la estrella para mostrarlo en TV.</p>}
      {featured.map((p) => <Row key={`f-${p.id}`} p={p} />)}
    </div>
  );
};

export const TvPromoManager = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ promos: [], featured: [] });
  const reload = () => axiosInstance.get("/lottery/tv-promos").then((r) => setData(r.data));
  useEffect(() => { if (open) reload(); }, [open]);
  return (
    <>
      <Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => setOpen(true)} data-testid="tv-promos-button"><Megaphone className="w-4 h-4" /> Publicidad TV</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tv-promos-dialog">
          <DialogHeader>
            <DialogTitle>Publicidad en Modo TV</DialogTitle>
            <DialogDescription>Después de cada país se muestra una promoción o producto destacado durante 10 segundos.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="promos">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="promos" data-testid="tv-tab-promos">Promociones ({data.promos.length})</TabsTrigger>
              <TabsTrigger value="products" data-testid="tv-tab-products">Productos ({data.featured.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="promos" className="mt-3"><PromosTab promos={data.promos} reload={reload} /></TabsContent>
            <TabsContent value="products" className="mt-3"><ProductsTab featured={data.featured} reload={reload} /></TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};
